import { ListFilter } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/core/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/core/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/core/popover';

import { FilterDefs, FilterState } from './types';

type Props = {
  filterDefs: FilterDefs;
  filterState: FilterState<FilterDefs>;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
};

export function AddFilterDropdown({ filterDefs, filterState, onSelect, onRemove }: Props) {
  const [open, setOpen] = useState(false);
  const selectedIds = useMemo(() => filterState.map((f) => f.id), [filterState]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button aria-label="add or remove filters" variant="outline" size="icon">
          <ListFilter />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-full max-w-72 min-w-48 p-0">
        <Command loop>
          <CommandInput placeholder="Search fields..." />
          <CommandList>
            <CommandEmpty>No fields found.</CommandEmpty>
            <CommandGroup>
              {Object.entries(filterDefs).map(([id, filterDef]) => {
                const isSelected = selectedIds.includes(id);
                return (
                  <CommandItem
                    key={id}
                    value={id}
                    data-checked={isSelected ? 'true' : undefined}
                    onSelect={() => {
                      if (isSelected) {
                        onRemove(id);
                      } else {
                        onSelect(id);
                        setOpen(false);
                      }
                    }}
                  >
                    {filterDef.icon && <filterDef.icon />}
                    <span className="truncate">{filterDef.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
